import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { SPACING } from 'Style/spacing';
import { BODY_TYPES } from 'Style/typography';
import { BREAKPOINTS } from 'Style/breakpoints';

import { TEXT_COLORS } from 'Style/colors';

// Utils
import classNames from 'classnames';
import { getImageAttribution } from 'Utils/image-util';

// Components
import WebLink from '../../../components/web-link';
import Image, { THEMES as ImageThemes } from '../../../components/base/image';
import withT from 'ComponentLibrary/hocs/withT';

import connector from 'Utils/connector';
import connectRouting from 'Webapp/shared/app/connectors/connectRouting';
import connectResponsive from 'Webapp/shared/app/connectors/connectResponsive';

const StoryboardHeaderCoverImageWrapper = styled.div(
  {
    position: 'relative',
  },
  BREAKPOINTS.phone({
    gridColumn: '1 / 2',
    gridRow: '1 / 3',
    left: '0px',
    top: '0px',
    width: '100%',

    /* CSS Hack to force a square size for the cover image.
     * It creates an empty div with of height of 100%
     * Percentages are always calculated off of the "width".
     **/
    '&:before': {
      content: '""',
      display: 'block',
      paddingTop: '100%',
      /* initial ratio of 1:1*/
    },
  }),
  BREAKPOINTS.tabletPortraitUp({
    gridColumn: '2 / 3',
    gridRow: '1 / 4',
  }),
);
const StoryboardHeaderCoverImageDiv = styled.div({
  position: 'absolute',
  top: '0',
  left: '0',
  bottom: '0',
  right: '0',
  '&:before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    top: '0',
    left: '0',
    bottom: '0',
    right: '0',
    zIndex: '2',
    background:
      'linear-gradient(180deg, rgba(0, 0, 0, 0) 75%, rgba(0, 0, 0, 0.7) 100%)',

    ...BREAKPOINTS.phone({
      background:
        'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%)',
    }),
  },
});
const StorybordHeaderCoverImageAttribution = styled.p(
  BODY_TYPES.XSMALL_STANDARD,
  {
    color: TEXT_COLORS.overlay,
    position: 'absolute',
    mixBlendMode: 'normal',
    opacity: '0.6',
    zIndex: '3',
  },
  BREAKPOINTS.phone({
    left: SPACING.LARGE,
    bottom: SPACING.LARGE,
  }),

  BREAKPOINTS.tabletPortraitUp({
    right: SPACING.BASE4X,
    bottom: SPACING.LARGE,
  }),
);

function getImageElement(imageElement, section, currentPath) {
  if (section.canonicalPath === currentPath) {
    return imageElement;
  }
  return <WebLink href={section.canonicalPath}>{imageElement}</WebLink>;
}

function StoryboardHeaderCoverImage({
  section,
  coverImage,
  coverImageSquare,
  isPhone,
  displayCoverAttribution,
  routing,
  t,
}) {
  if (!coverImage) {
    return null;
  }

  const { original_width, original_height } = coverImage;
  const coverImageAspectRatio =
    Number.isInteger(original_width) &&
    Number.isInteger(original_height) &&
    original_width / original_height;
  const noMobileCrop = coverImageAspectRatio >= 1 && isPhone;

  const coverImageClassName = classNames({
    // 'storyboard-header__cover-image': true,
    'storyboard-header__cover-image--no-mobile-crop': noMobileCrop,
    'storyboard-header__cover-image--with-square-image': !!coverImageSquare,
  });

  const imageProps = {
    image: coverImage,
    supportResize: true,
    variant: ImageThemes.large,
    ampLayout: 'responsive',
    alt: coverImage.title || section.title,
  };

  const attributionText = getImageAttribution(coverImage, t);

  // Don't crop non-square header images on phone-sized viewports
  return (
    <React.Fragment>
      {coverImage && (
        <StoryboardHeaderCoverImageWrapper>
          <StoryboardHeaderCoverImageDiv className={coverImageClassName}>
            {getImageElement(
              <Image {...imageProps} supportResize crop />,
              section,
              routing.pathname,
            )}
            {attributionText && displayCoverAttribution && (
              <StorybordHeaderCoverImageAttribution>
                {attributionText}
              </StorybordHeaderCoverImageAttribution>
            )}
          </StoryboardHeaderCoverImageDiv>
        </StoryboardHeaderCoverImageWrapper>
      )}
      {coverImageSquare && (
        <div className="storyboard-header__cover-image--square">
          {getImageElement(
            <Image
              image={coverImageSquare}
              supportResize
              variant={ImageThemes.large}
              ampLayout="responsive"
              alt={coverImageSquare.title || section.title}
              crop
            />,
            section,
            routing.pathname,
          )}
        </div>
      )}
    </React.Fragment>
  );
}

StoryboardHeaderCoverImage.propTypes = {
  section: PropTypes.object.isRequired,
  coverImage: PropTypes.object.isRequired,
  routing: PropTypes.object.isRequired,
  coverImageSquare: PropTypes.object,
  isPhone: PropTypes.bool.isRequired,
  displayCoverAttribution: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

StoryboardHeaderCoverImage.defaultProps = {
  coverImageSquare: null,
};

export default connector(
  connectResponsive,
  connectRouting,
)(withT(StoryboardHeaderCoverImage));
